import { useMemo, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  REGEX,
  PASSWORD_POLICY,
  SIGNUP_CODE_CHARS,
  getCountryList,
  RequestStatus,
  TERMS_CONDITIONS_URL,
  TERMS_CONDITIONS_URL_CH,
  PRIVACY_POLICY_URL,
  PRIVACY_POLICY_URL_CH,
  SIGNUP_USER_TYPE_OPTIONS,
} from "@apptractive/stm-shared";
import {
  WBAlert,
  WBButton,
  WBFlex,
  WBForm,
  WBIconButton,
  WBLink,
  WBModal,
  WBSelect,
  WBTextField,
  WBTypography,
} from "@apptractive/design-system-web";

export interface SignUpFormData {
  firstName: string;
  lastName: string;
  phone?: string;
  email: string;
  password: string;
  code: string;
  country?: string;
  userType?: string;
}

interface SignUpFormProps {
  authError: any;
  authStatus: RequestStatus;
  codeDelivery: string;
  onSubmit: SubmitHandler<any>;
  onResendCode: Function;
}

const SignUpForm = ({
  authError,
  authStatus,
  codeDelivery,
  onSubmit,
  onResendCode,
}: SignUpFormProps) => {
  const { t, i18n } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const passwordInput = useRef(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const userOptions = useMemo(
    () =>
      SIGNUP_USER_TYPE_OPTIONS.map((option) => ({
        ...option,
        label: t(option.label, { ns: "options" }),
      })) || {},
    [t]
  );

  const { country } = watch();

  const inputs = useMemo(
    () => ({
      firstName: {
        label: t("firstNameTitle", { ns: "common" }),
        name: "firstName",
        type: "text",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("firstNameRequired", { ns: "common" }),
          //pattern: {
          //  message: t("firstNameInvalid")
          //}
        },
      },
      lastName: {
        label: t("lastNameTitle", { ns: "common" }),
        name: "lastName",
        type: "text",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("lastNameRequired", { ns: "common" }),
          //pattern: {
          //  message: t("lastNameInvalid")
          //}
        },
      },
      country: {
        label: t("countryTitle", { ns: "common" }),
        placeholder: t("countryPlaceholder", { ns: "common" }),
        options: getCountryList(i18n.resolvedLanguage),
        name: "country",
        defaultValue: "",
        rules: {
          required: t("countryRequired", { ns: "common" }),
        },
      },
      userType: {
        label: t("userTypeTitle", { ns: "common" }),
        placeholder: t("userTypePlaceholder", { ns: "common" }),
        options: userOptions,
        name: "userType",
        defaultValue: "",
        rules: {
          required: t("userTypeRequired", { ns: "common" }),
        },
      },
      email: {
        label: t("emailTitle", { ns: "common" }),
        name: "email",
        type: "email",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("emailRequired", { ns: "common" }),
          pattern: {
            value: REGEX.EMAIL,
            message: t("invalidEmail", { ns: "common" }),
          },
        },
      },
      phone: {
        label: t("mobileTitle", { ns: "common" }),
        name: "phone",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("mobileRequired", { ns: "common" }),
        },
      },
      password: {
        label: t("passwordTitle", { ns: "common" }),
        name: "password",
        type: "password",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("passwordRequired", { ns: "common" }),
          minLength: {
            value: PASSWORD_POLICY.length,
            message: `${t("passwordMustBe", { ns: "common" })} ${
              PASSWORD_POLICY.length
            } ${t("orMoreCharacters", { ns: "common" })}`,
          },
        },
      },
      code: {
        name: "code",
        label: t("codeTitle", { ns: "common" }),
        placeholder: "",
        defaultValue: "",
        type: "number",
        rules: {
          required: t("codeRequired", { ns: "common" }),
          minLength: {
            value: SIGNUP_CODE_CHARS,
            message: `${t("codeMustBe", {
              ns: "common",
            })} ${SIGNUP_CODE_CHARS} ${t("characters", { ns: "common" })}`,
          },
        },
      },
    }),
    [t, i18n, userOptions]
  );

  /**
   * Reset code field and trigger resend new code
   */
  const onResendCodePress = async () => {
    setValue("code", "");

    onResendCode();
  };

  return (
    <>
      <WBModal
        open={showInfo}
        onClose={() => setShowInfo(false)}
        sx={{ width: "95%" }}
      >
        <WBFlex flexDirection="column">
          <WBTypography variant="h3">
            {t("brandInfoTitle", { ns: "signUp" })}
          </WBTypography>
          <WBTypography>
            {t("brandInfoDescription", { ns: "signUp" })}
          </WBTypography>

          <WBTypography variant="h3" mt={3}>
            {t("journalistInfoTitle", { ns: "signUp" })}
          </WBTypography>
          <WBTypography>
            {" "}
            {t("journalistInfoDescription", { ns: "signUp" })}
          </WBTypography>
        </WBFlex>
      </WBModal>

      <WBForm onSubmit={handleSubmit(onSubmit)}>
        {codeDelivery ? (
          <Controller
            control={control}
            name={inputs.code.name}
            rules={inputs.code.rules}
            defaultValue={inputs.code.defaultValue}
            render={({ field }) => (
              <WBTextField
                {...field}
                type={inputs.code.type}
                label={inputs.code.label}
                //placeholder={inputs.email.placeholder}
                error={!!(errors.code && errors.code.message)}
                helperText={(errors.code && errors.code.message) || ""}
                margin="dense"
              />
            )}
          />
        ) : (
          <>
            {/*<Controller*/}
            {/*  control={control}*/}
            {/*  name={inputs.firstName.name}*/}
            {/*  rules={inputs.firstName.rules}*/}
            {/*  defaultValue={inputs.firstName.defaultValue}*/}
            {/*  render={({ field }) => (*/}
            {/*    <WBTextField*/}
            {/*      {...field}*/}
            {/*      type="text"*/}
            {/*      label={inputs.firstName.label}*/}
            {/*      //placeholder={inputs.firstName.placeholder}*/}
            {/*      error={!!(errors.firstName && errors.firstName.message)}*/}
            {/*      helperText={*/}
            {/*        (errors.firstName && errors.firstName.message) || ""*/}
            {/*      }*/}
            {/*      margin="dense"*/}
            {/*    />*/}
            {/*  )}*/}
            {/*/>*/}
            {/*<Controller*/}
            {/*  control={control}*/}
            {/*  name={inputs.lastName.name}*/}
            {/*  rules={inputs.lastName.rules}*/}
            {/*  defaultValue={inputs.lastName.defaultValue}*/}
            {/*  render={({ field }) => (*/}
            {/*    <WBTextField*/}
            {/*      {...field}*/}
            {/*      type="text"*/}
            {/*      label={inputs.lastName.label}*/}
            {/*      //placeholder={inputs.lastName.placeholder}*/}
            {/*      error={!!(errors.lastName && errors.lastName.message)}*/}
            {/*      helperText={*/}
            {/*        (errors.lastName && errors.lastName.message) || ""*/}
            {/*      }*/}
            {/*      margin="dense"*/}
            {/*    />*/}
            {/*  )}*/}
            {/*/>*/}
            <Controller
              control={control}
              name={inputs.country.name}
              rules={inputs.country.rules}
              defaultValue={inputs.country.defaultValue}
              render={({ field: { onChange, ...props } }) => (
                <WBSelect
                  {...props}
                  onChange={(e) => onChange(e?.target?.value)}
                  label={inputs.country.label}
                  options={inputs.country.options}
                  //placeholder={inputs.email.placeholder}
                  error={!!(errors.country && errors.country.message)}
                  helperText={(errors.country && errors.country.message) || ""}
                  margin="dense"
                />
              )}
            />
            <WBFlex>
              <Controller
                control={control}
                name={inputs.userType.name}
                rules={inputs.userType.rules}
                defaultValue={inputs.userType.defaultValue}
                render={({ field: { onChange, ...props } }) => (
                  <WBSelect
                    {...props}
                    onChange={(e) => onChange(e?.target?.value)}
                    label={inputs.userType.label}
                    options={inputs.userType.options}
                    //placeholder={inputs.email.placeholder}
                    error={!!(errors.userType && errors.userType.message)}
                    helperText={
                      (errors.userType && errors.userType.message) || ""
                    }
                    margin="dense"
                  />
                )}
              />
              <WBIconButton
                icon="InformationCircle"
                onClick={() => setShowInfo(true)}
              />
            </WBFlex>
            <Controller
              control={control}
              name={inputs.email.name}
              rules={inputs.email.rules}
              defaultValue={inputs.email.defaultValue}
              render={({ field }) => (
                <WBTextField
                  {...field}
                  type={inputs.email.type}
                  label={inputs.email.label}
                  //placeholder={inputs.email.placeholder}
                  error={!!(errors.email && errors.email.message)}
                  helperText={(errors.email && errors.email.message) || ""}
                  margin="dense"
                />
              )}
            />
            {/*<Controller*/}
            {/*  control={control}*/}
            {/*  name={inputs.phone.name}*/}
            {/*  rules={inputs.phone.rules}*/}
            {/*  defaultValue={inputs.phone.defaultValue}*/}
            {/*  render={({ field }) => (*/}
            {/*    <WBPHInput*/}
            {/*      {...field}*/}
            {/*      label={inputs.phone.label}*/}
            {/*      defaultCountry={"AU"}*/}
            {/*      withCountryCallingCode={true}*/}
            {/*      error={!!(errors.phone && errors.phone.message)}*/}
            {/*      helperText={(errors.phone && errors.phone.message) || ""}*/}
            {/*    />*/}
            {/*  )}*/}
            {/*/>*/}
            <Controller
              control={control}
              name={inputs.password.name}
              rules={inputs.password.rules}
              defaultValue={inputs.password.defaultValue}
              render={({ field }) => (
                <WBTextField
                  {...field}
                  type={isPasswordVisible ? "text" : "password"}
                  inputRef={passwordInput}
                  label={inputs.password.label}
                  rightIcon={
                    <WBIconButton
                      icon={isPasswordVisible ? "EyeOff" : "Eye"}
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    />
                  }
                  error={!!(errors.password && errors.password.message)}
                  helperText={
                    (errors.password && errors.password.message) || ""
                  }
                  margin="dense"
                />
              )}
            />
          </>
        )}

        {!codeDelivery && (
          <WBTypography mb={1} mt={1}>
            {t("signUpAgreeTo", { ns: "signUp" })}{" "}
            <WBLink
              href={
                country === "CH"
                  ? TERMS_CONDITIONS_URL_CH
                  : TERMS_CONDITIONS_URL
              }
              target="_blank"
            >
              {t("termsConditionsTitle", { ns: "common" })}
            </WBLink>{" "}
            {t("and", { ns: "signUp" })}{" "}
            <WBLink
              href={
                country === "CH" ? PRIVACY_POLICY_URL_CH : PRIVACY_POLICY_URL
              }
              target="_blank"
            >
              {t("privacyPolicyTitle", { ns: "common" })}
            </WBLink>
          </WBTypography>
        )}

        {authError?.code && (
          <WBAlert
            title={t(authError.code, { ns: "authentication" })}
            severity="error"
          />
        )}

        <WBFlex>
          <WBButton sx={{ mt: 1 }} loading={authStatus === "submitting"}>
            {codeDelivery
              ? t("signUpTitle", { ns: "common" })
              : t("proceedTitle", { ns: "common" })}
          </WBButton>
        </WBFlex>

        {codeDelivery && (
          <WBFlex justifyContent="center">
            <WBButton
              variant="text"
              uppercase={false}
              type="button"
              onClick={() => onResendCodePress()}
            >
              {t("resendCodeTitle", { ns: "common" })}
            </WBButton>
          </WBFlex>
        )}
      </WBForm>
    </>
  );
};

export default SignUpForm;
